import clsx from 'clsx';
import { useEffect, useState } from 'react';
import styled from 'styled-components/macro';

import { Defaults, setDefaults } from '../common';

export const ThemeSwitch = ({ className }: { className?: string }) => {
  const [theme, setTheme] = useState<'light' | 'dark'>(
    (localStorage.getItem('theme') as 'light' | 'dark') || 'light',
  );

  useEffect(() => {
    const theme = localStorage.getItem('theme');
    if (!theme) {
      setTheme('light');
      localStorage.setItem('theme', 'light');
      setDefaults('light');
      window.location.reload();
    }
  }, []);

  const handleThemeChange = (theme: 'light' | 'dark') => () => {
    setTheme(theme);
    localStorage.setItem('theme', theme);
    setDefaults(theme);
    window.location.reload();
  };

  return (
    <ThemeSwitchRoot className={className}>
      <IconRoot
        className={clsx(theme === 'light' && 'active')}
        onClick={handleThemeChange('light')}>
        <LightIcon fill={theme === 'light' ? '#000000' : '#8B959E'} />
      </IconRoot>
      <IconRoot
        className={clsx(theme === 'dark' && 'active')}
        onClick={handleThemeChange('dark')}>
        <DarkIcon fill={theme === 'dark' ? '#000000' : '#8B959E'} />
      </IconRoot>
    </ThemeSwitchRoot>
  );
};

const LightIcon = ({ fill }: { fill: string }) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12 24L8.31818 20.8636C8.31818 20.8636 10.3636 21.5455 12 21.5455C13.6364 21.5455 15.6818 20.8636 15.6818 20.8636L12 24Z"
      fill={fill}
    />
    <path
      d="M3.13636 15.6818C3.13636 15.6818 4.02473 17.6611 5.18182 18.8182C6.33893 19.9752 8.31818 20.8636 8.31818 20.8636L3.51483 20.4852L3.13636 15.6818Z"
      fill={fill}
    />
    <path
      d="M20.4854 20.4852L15.6818 20.8636C15.6818 20.8636 17.6611 19.9752 18.8182 18.8182C19.9753 17.6611 20.8636 15.6818 20.8636 15.6818L20.4854 20.4852Z"
      fill={fill}
    />
    <path
      d="M12 3.54545C16.6693 3.54545 20.4545 7.33068 20.4545 12C20.4545 16.6693 16.6693 20.4545 12 20.4545C7.33068 20.4545 3.54545 16.6693 3.54545 12C3.54545 7.33068 7.33068 3.54545 12 3.54545Z"
      fill={fill}
    />
    <path
      d="M3.13636 8.31818C3.13636 8.31818 2.45455 10.3636 2.45455 12C2.45455 13.6364 3.13636 15.6818 3.13636 15.6818L0 12L3.13636 8.31818Z"
      fill={fill}
    />
    <path
      d="M24 12L20.8636 15.6818C20.8636 15.6818 21.5455 13.6364 21.5455 12C21.5455 10.3636 20.8636 8.31818 20.8636 8.31818L24 12Z"
      fill={fill}
    />
    <path
      d="M8.31818 3.13636C8.31818 3.13636 6.33894 4.0248 5.18182 5.18182C4.02473 6.3389 3.13636 8.31818 3.13636 8.31818L3.51483 3.51483L8.31818 3.13636Z"
      fill={fill}
    />
    <path
      d="M20.4857 3.51483L20.8636 8.31818C20.8636 8.31818 19.9753 6.3389 18.8182 5.18182C17.6611 4.02478 15.6821 3.13636 15.6821 3.13636L20.4857 3.51483Z"
      fill={fill}
    />
    <path
      d="M15.6821 3.13636C15.6821 3.13636 13.6364 2.45455 12 2.45455C10.3636 2.45455 8.31818 3.13636 8.31818 3.13636L12 0L15.6821 3.13636Z"
      fill={fill}
    />
  </svg>
);

const DarkIcon = ({ fill }: { fill: string }) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg">
    <path
      d="M17.7939 0C21.5156 2.27377 24 6.37389 24 11.0545C24 18.204 18.204 24 11.0545 24C6.37389 24 2.27377 21.5156 0 17.7939C1.96317 18.9933 4.27062 19.6848 6.73961 19.6848C13.8891 19.6848 19.6848 13.8891 19.6848 6.73961C19.6848 4.27062 18.9933 1.96317 17.7939 0Z"
      fill={fill}
    />
    <path
      d="M2.18182 9.81818C2.18182 9.81818 2.38769 10.879 2.84526 11.3366C3.30274 11.794 4.36364 12 4.36364 12C4.36364 12 3.30275 12.206 2.84526 12.6634C2.38777 13.1209 2.18182 14.1818 2.18182 14.1818C2.18182 14.1818 1.97585 13.1209 1.51838 12.6634C1.0608 12.2059 0 12 0 12C0 12 1.06083 11.7941 1.51838 11.3366C1.97592 10.879 2.18182 9.81818 2.18182 9.81818Z"
      fill={fill}
    />
    <path
      d="M14.1818 4.36364C14.1818 4.36364 14.4905 5.95498 15.1768 6.64134C15.8631 7.32755 17.4539 7.63624 17.4545 7.63636C17.4539 7.63648 15.8631 7.94516 15.1768 8.63139C14.4906 9.31762 14.1819 10.9085 14.1818 10.9091C14.1817 10.9084 13.873 9.31761 13.1868 8.63139C12.5004 7.94503 10.9091 7.63636 10.9091 7.63636C10.9091 7.63636 12.5005 7.32765 13.1868 6.64134C13.8731 5.95502 14.1818 4.36364 14.1818 4.36364Z"
      fill={fill}
    />
    <path
      d="M6.54545 0C6.54545 0 6.95719 2.12161 7.87234 3.03675C8.78729 3.95171 10.9082 4.36347 10.9091 4.36364C10.9083 4.36379 8.78731 4.77555 7.87234 5.69052C6.95736 6.60549 6.54561 8.72647 6.54545 8.72727C6.54528 8.72639 6.13353 6.60547 5.21857 5.69052C4.30343 4.77537 2.18182 4.36364 2.18182 4.36364C2.18182 4.36364 4.30348 3.95184 5.21857 3.03675C6.13366 2.12167 6.54545 0 6.54545 0Z"
      fill={fill}
    />
  </svg>
);

const IconRoot = styled.div`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  cursor: pointer;

  @media (max-width: 1024px) {
    width: 32px;
    height: 32px;
  }

  &.active {
    background-color: ${Defaults.selectActiveColor};
  }
`;

const ThemeSwitchRoot = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  background-color: ${Defaults.inputsBgColor};
  border-radius: 8px;
  padding: 4px;
`;
